import { useState } from "react";
import { PICKS } from "@/mocks/gossip";

const LIKE_BASE: Record<string, number> = { p1: 8621, p2: 5310, p3: 2960, p4: 6142, p5: 2418, p6: 1503 };

function ArticleCard({ p, featured, liked, onLike }: {
  p: { id: string; title: string; category: string; excerpt: string; readTime: string; date: string; views: string; comment: string; thumb: string; hot?: boolean };
  featured?: boolean;
  liked: boolean;
  onLike: () => void;
}) {
  return (
    <article
      className={`group overflow-hidden rounded-2xl border border-ink-200/70 bg-paper-50 card-lift hover:-translate-y-1 hover:shadow-lift ${
        featured ? "lg:flex" : ""
      }`}
    >
      <div className={`relative overflow-hidden ${featured ? "lg:w-1/2 h-52 lg:h-auto" : "h-44"}`}>
        <img
          src={p.thumb}
          alt={p.title}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
        />
        {p.hot && (
          <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-brand-500 text-paper-50 text-xs font-bold flex items-center gap-1">
            <i className="ri-fire-fill text-sm" /> 热文
          </span>
        )}
      </div>
      <div className={`flex flex-col flex-1 p-5 ${featured ? "lg:p-7 lg:justify-center" : ""}`}>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs px-2 py-0.5 rounded bg-brand-50 text-brand-600 font-semibold">{p.category}</span>
          <span className="text-xs text-ink-400">{p.readTime}</span>
        </div>
        <h3 className={`font-heading font-bold text-ink-900 mt-3 leading-snug group-hover:text-brand-600 transition-colors ${featured ? "text-xl md:text-2xl" : "text-base"}`}>
          {p.title}
        </h3>
        <p className={`text-sm text-ink-600 leading-relaxed mt-2 ${featured ? "" : "line-clamp-2"}`}>{p.excerpt}</p>
        <div className="mt-4 pt-4 border-t border-ink-200/60 flex items-center gap-4 text-xs text-ink-500">
          <span className="inline-flex items-center gap-1"><i className="ri-calendar-line text-ink-400" />{p.date}</span>
          <span className="inline-flex items-center gap-1"><i className="ri-eye-line text-ink-400" />{p.views}</span>
          <button
            onClick={onLike}
            aria-pressed={liked}
            className={`ml-auto inline-flex items-center gap-1 px-2.5 h-8 rounded-full transition-colors ${
              liked ? "text-rose-600 bg-rose-100" : "text-ink-500 hover:text-rose-600 hover:bg-rose-50"
            }`}
          >
            <i className={liked ? "ri-heart-fill" : "ri-heart-line"} />
            {((LIKE_BASE[p.id] || 0) + (liked ? 1 : 0)).toLocaleString()}
          </button>
        </div>
      </div>
    </article>
  );
}

export default function EditorPicks() {
  const [liked, setLiked] = useState<Record<string, boolean>>({});
  const toggle = (id: string) => setLiked((p) => ({ ...p, [id]: !p[id] }));

  const featured = PICKS[0];
  const tall = PICKS[1];
  const rest = PICKS.slice(2);

  return (
    <section id="editor" className="bg-paper-50 py-12 md:py-20 scroll-mt-24">
      <div className="max-w-[1200px] mx-auto px-4 md:px-6">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-brand-600 mb-2 kicker-line">编辑部出品</p>
            <h2 className="font-heading font-black text-3xl md:text-4xl text-ink-900">编辑精选</h2>
            <p className="mt-2 text-sm text-ink-500">把热门瓜拆开讲明白：传播链、公关动作与时间线复盘，读过再站队。</p>
          </div>
          <span className="text-xs text-ink-400">本周已更新 12 篇 · 数据持续刷新</span>
        </div>

        {/* 头条 + 二号位 非对称布局 */}
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-5">
          <div className="lg:col-span-7">
            <ArticleCard p={featured} featured liked={!!liked[featured.id]} onLike={() => toggle(featured.id)} />
          </div>
          <div className="lg:col-span-5">
            <ArticleCard p={tall} liked={!!liked[tall.id]} onLike={() => toggle(tall.id)} />
          </div>
        </div>

        {/* 其余精选 四列 */}
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {rest.map((p) => (
            <ArticleCard key={p.id} p={p} liked={!!liked[p.id]} onLike={() => toggle(p.id)} />
          ))}
        </div>
      </div>
    </section>
  );
}